import api from "./client";

export const discoveryAPI = {
  // The axios response interceptor does NOT unwrap, so each method returns
  // response.data — matching the rest of this file's convention.
  ask: async ({ question, mode = "auto", conversationId, source }) =>
    (await api.post("/api/v1/discovery/ask", { question, mode, conversation_id: conversationId, source })).data,
  listConversations: async () => (await api.get("/api/v1/discovery/conversations")).data,
  getConversation: async (id) => (await api.get(`/api/v1/discovery/conversations/${id}`)).data,
  deleteConversation: async (id) => (await api.delete(`/api/v1/discovery/conversations/${id}`)).data,
};

export const discoveryQuestionsAPI = {
  getQuestions: async (clientId) => {
    const response = await api.get("/api/v1/discovery_questions", { params: { client_id: clientId } });
    return response.data;
  },
  updateStatus: async (clientId, questionId, status) => {
    const response = await api.patch(`/api/v1/discovery_questions/${questionId}`, { client_id: clientId, status });
    return response.data;
  },
  generate: async (clientId) => {
    const response = await api.post("/api/v1/discovery_questions/generate", { client_id: clientId });
    return response.data;
  },
};

export const promptOptimizerAPI = {
  getAllOptimizations: async (clientId, agentId = null) => {
    const params = { client_id: clientId };
    if (agentId) params.agent_id = agentId;
    const response = await api.get("/api/v1/prompt_optimizations", { params });
    return response.data;
  },

  getOptimization: async (optimizationId, clientId) => {
    const response = await api.get(
      `/api/v1/prompt_optimizations/${optimizationId}`,
      { params: { client_id: clientId } }
    );
    return response.data;
  },

  createOptimization: async (agentId, sourceItems, preferredModel, constraints, clientId, mode) => {
    const response = await api.post("/api/v1/prompt_optimizations", {
      agent_id: agentId,
      source_items: sourceItems,
      preferred_model: preferredModel,
      improvement_constraints: constraints,
      mode,
      client_id: clientId,
    });
    return response.data;
  },

  getOptimizationStatus: async (id, clientId) => {
    const response = await api.get(
      `/api/v1/prompt_optimizations/${id}/status`,
      { params: { client_id: clientId } }
    );
    return response.data;
  },

  updateOptimization: async (id, newInstructions, clientId) => {
    const response = await api.put(`/api/v1/prompt_optimizations/${id}`, {
      new_instructions: newInstructions,
      client_id: clientId,
    });
    return response.data;
  },

  mergeOptimization: async (id, commitMessage, clientId) => {
    const response = await api.post(
      `/api/v1/prompt_optimizations/${id}/merge`,
      { commit_message: commitMessage, client_id: clientId }
    );
    return response.data;
  },

  rejectOptimization: async (id, clientId) => {
    const response = await api.post(
      `/api/v1/prompt_optimizations/${id}/reject`,
      { client_id: clientId }
    );
    return response.data;
  },

  reset: async (agentId, targetOptimizationId, clientId) => {
    const response = await api.post("/api/v1/prompt_optimizations/reset", {
      agent_id: agentId,
      target_optimization_id: targetOptimizationId,
      client_id: clientId,
    });
    return response.data;
  },

  diagnose: async (id, { sourceItems, preferredModel, constraints, mode }, clientId) => {
    const res = await api.post(`/api/v1/prompt_optimizations/${id}/diagnose`, {
      source_items: sourceItems,
      preferred_model: preferredModel,
      improvement_constraints: constraints,
      mode,
      client_id: clientId,
    });
    return res.data;
  },

  generate: async (id, selectedGapIds, clientId, gaps) => {
    const body = { selected_gap_ids: selectedGapIds, client_id: clientId };
    if (gaps) body.gaps = gaps; // user-edited diagnosis gaps (optional)
    const res = await api.post(`/api/v1/prompt_optimizations/${id}/generate`, body);
    return res.data;
  },
};

// Pipecat provider catalogue — schema-driven config fields per LLM/TTS/ASR provider
export const pipecatAPI = {
  getProviders: async () => {
    const response = await api.get('/api/v1/pipecat/providers');
    return response.data;
  },
};

// Twilio phone numbers — list available numbers for telephony config
export const twilioAPI = {
  getPhoneNumbers: async () => {
    const response = await api.get('/api/v1/twilio/phone_numbers');
    return response.data;
  },
};

// Workspace-scoped phone numbers (for Outbound Caller ID picker on agents)
export const clientPhoneNumbersAPI = {
  list: async (clientId) => {
    const response = await api.get(`/api/v1/clients/${clientId}/phone_numbers`);
    return response.data.data; // { default, assigned, is_internal_admin }
  },
};

export const integrationAPI = {
  get: async (clientId, agentId) => {
    const response = await api.get(`/api/v1/agents/${agentId}/integration`, {
      params: { client_id: clientId },
    });
    return response.data;
  },

  create: async (clientId, agentId, data) => {
    const response = await api.post(`/api/v1/agents/${agentId}/integration`, {
      integration: data,
    }, { params: { client_id: clientId } });
    return response.data;
  },

  update: async (clientId, agentId, data) => {
    const response = await api.patch(`/api/v1/agents/${agentId}/integration`, {
      integration: data,
    }, { params: { client_id: clientId } });
    return response.data;
  },

  destroy: async (clientId, agentId) => {
    const response = await api.delete(`/api/v1/agents/${agentId}/integration`, {
      params: { client_id: clientId },
    });
    return response.data;
  },

  syncInstructions: async (clientId, agentId) => {
    const response = await api.post(`/api/v1/agents/${agentId}/integration/sync_instructions`, null, {
      params: { client_id: clientId },
    });
    return response.data;
  },

  // Fetch a vendor assistant's config before the agent is saved (create screen).
  previewVendorConfig: async (clientId, vendor, assistantId) => {
    const response = await api.post(`/api/v1/agents/preview_vendor_config`, null, {
      params: { client_id: clientId, vendor, assistant_id: assistantId },
    });
    return response.data;
  },

  fetchCalls: async (clientId, agentId, fetchFrom) => {
    const response = await api.post(`/api/v1/agents/${agentId}/integration/fetch_calls`, null, {
      params: { client_id: clientId, ...(fetchFrom ? { from: fetchFrom } : {}) },
    });
    return response.data;
  },

  pushInstructions: async (clientId, agentId, optimizationId) => {
    const res = await api.post(`/api/v1/agents/${agentId}/integration/push_instructions`, null, {
      params: { client_id: clientId, optimization_id: optimizationId },
    });
    return res.data;
  },
};
